import { useState, useEffect, useRef } from "react";
import { Search, Map, X, Phone, CheckCircle, Clock, Download, Home, Lock } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Export PDF simple
async function exportPDF(ref, name) {
  if (!ref.current) return;
  const canvas = await html2canvas(ref.current, { scale: 2, useCORS: true, backgroundColor: '#FDF8F3' });
  const pdf = new jsPDF({ orientation: canvas.width > canvas.height ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  const r = Math.min((w - 20) / canvas.width, (h - 20) / canvas.height);
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', (w - canvas.width * r) / 2, 10, canvas.width * r, canvas.height * r);
  pdf.save(`parcours_${name.replace(/\s+/g, '_')}.pdf`);
}

// Helper pour compter les étapes complétées
function countDone(ids, data) {
  let count = 0;
  for (let i = 0; i < ids.length; i++) {
    if (data[ids[i]] && data[ids[i]].trim()) count++;
  }
  return count;
}

// Vue Parchemin horizontale
function ViewParchemin({ client, etapes, onClose, onToggle }) {
  const ref = useRef(null);
  const [exp, setExp] = useState(false);
  
  const phases = [
    { n: 1, t: "Prise de contact", c: "#F97316", bg: "#FFF7ED", i: "📅", e: [
      ["datePriseRdv", "Date de prise"],
      ["dateR1", "RDV audit"],
      ["resultatAppelQualite", "Appel qualité"]
    ]},
    { n: 2, t: "Accompagnement AMO", c: "#22C55E", bg: "#F0FDF4", i: "📋", e: [
      ["amoMandate", "AMO mandaté"],
      ["transfereAmo", "Transfert AMO"],
      ["vtAmo", "Visite technique"]
    ]},
    { n: 3, t: "Suivi & Devis", c: "#EAB308", bg: "#FEFCE8", i: "📝", e: [
      ["etatDevis", "État devis"],
      ["dateR2", "Date R2"],
      ["archiEnCharge", "Architecte"]
    ]},
    { n: 4, t: "Aides financières", c: "#84CC16", bg: "#F7FEE7", i: "💰", e: [
      ["dateDepotAnah", "Dépôt ANAH"],
      ["dateOctroi", "Octroi aides"],
      ["dateEncaissementAvance", "Avance reçue"]
    ]},
    { n: 5, t: "Travaux", c: "#14B8A6", bg: "#F0FDFA", i: "🔧", e: [
      ["dateDebutChantier", "Début travaux"],
      ["equipeInstallation", "Équipe pose"],
      ["dateFinChantier", "Fin travaux"]
    ]},
    { n: 6, t: "Réception & SAV", c: "#0EA5E9", bg: "#F0F9FF", i: "🏠", e: [
      ["dateReceptionChantier", "Réception"],
      ["reserveOuPas", "Réserves"],
      ["sav", "SAV"]
    ]}
  ];
  
  let total = 0;
  for (let p of phases) {
    const ids = [p.e[0][0], p.e[1][0], p.e[2][0]];
    if (countDone(ids, etapes) === ids.length) total++;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto">
      <div className="relative w-full max-w-5xl">
        <div className="absolute -top-3 -right-3 z-20 flex gap-2">
          <button onClick={async () => { setExp(true); await exportPDF(ref, client.nom + "_" + client.prenom); setExp(false); }} disabled={exp} className="w-11 h-11 bg-green-500 hover:bg-green-400 disabled:bg-green-700 rounded-full shadow-lg flex items-center justify-center">
            {exp ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Download className="w-5 h-5 text-white" />}
          </button>
          <button onClick={onClose} className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100"><X className="w-5 h-5 text-gray-600" /></button>
        </div>
        <div ref={ref} className="rounded-3xl shadow-2xl overflow-hidden" style={{ background: 'linear-gradient(180deg, #FDF8F3 0%, #FFFBF5 100%)' }}>
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #FFD93D 0%, #F7B731 100%)' }}><Home className="w-6 h-6 text-green-700" /></div>
                <div><h1 className="text-xl font-bold text-gray-800">Parcours client</h1><p className="text-sm text-gray-500">{client.nom} {client.prenom} • Dép. {client.dpt}</p></div>
              </div>
              <div className="px-4 py-2 rounded-full font-bold text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}>{total}/6</div>
            </div>
          </div>
          <div className="px-4 pb-6 overflow-x-auto">
            <div className="flex items-start gap-2 min-w-max py-4">
              <PhaseCard p={phases[0]} etapes={etapes} last={false} />
              <PhaseCard p={phases[1]} etapes={etapes} last={false} />
              <PhaseCard p={phases[2]} etapes={etapes} last={false} />
              <PhaseCard p={phases[3]} etapes={etapes} last={false} />
              <PhaseCard p={phases[4]} etapes={etapes} last={false} />
              <PhaseCard p={phases[5]} etapes={etapes} last={true} />
            </div>
          </div>
        </div>
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-white/95 backdrop-blur rounded-full shadow-xl px-2 py-1.5 flex gap-1 border border-gray-200">
          <button className="px-4 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-700">📜 Parchemin</button>
          <button onClick={onToggle} className="px-4 py-2 rounded-full text-sm font-medium text-gray-500 hover:bg-gray-100">📋 Pas à pas</button>
        </div>
      </div>
    </div>
  );
}

// Carte de phase pour Parchemin
function PhaseCard({ p, etapes, last }) {
  const ids = [p.e[0][0], p.e[1][0], p.e[2][0]];
  const done = countDone(ids, etapes);
  const stat = done === ids.length ? "ok" : done > 0 ? "wip" : "todo";
  
  // Récupérer les valeurs avec labels
  const e0 = { label: p.e[0][1], val: etapes[p.e[0][0]] };
  const e1 = { label: p.e[1][1], val: etapes[p.e[1][0]] };
  const e2 = { label: p.e[2][1], val: etapes[p.e[2][0]] };
  
  return (
    <div className="flex items-start">
      <div className="w-44 flex-shrink-0 relative">
        <div className="text-center mb-2">
          <div className="w-14 h-14 rounded-xl mx-auto flex items-center justify-center text-2xl shadow-md" style={{ backgroundColor: p.c + "20", border: "2px solid " + p.c }}>{p.i}</div>
        </div>
        <h3 className="text-sm font-bold text-center mb-2" style={{ color: p.c }}>{p.t}</h3>
        <div className="rounded-xl overflow-hidden shadow-md mx-1" style={{ background: p.bg, border: "2px solid " + p.c + "40" }}>
          <div className="p-3 space-y-1.5">
            <div className="text-xs">
              <span className="font-semibold text-gray-700">{e0.label}:</span>
              <span className={e0.val ? " text-gray-600" : " text-gray-400 italic"}> {e0.val || "(à remplir)"}</span>
            </div>
            <div className="text-xs">
              <span className="font-semibold text-gray-700">{e1.label}:</span>
              <span className={e1.val ? " text-gray-600" : " text-gray-400 italic"}> {e1.val || "(à remplir)"}</span>
            </div>
            <div className="text-xs">
              <span className="font-semibold text-gray-700">{e2.label}:</span>
              <span className={e2.val ? " text-gray-600" : " text-gray-400 italic"}> {e2.val || "(à remplir)"}</span>
            </div>
          </div>
          <div className="px-3 pb-3">
            <div className="w-full py-1.5 rounded-lg text-center text-xs font-bold text-white shadow-sm" style={{ backgroundColor: stat === "ok" ? "#22C55E" : stat === "wip" ? p.c : "#F97316" }}>
              {stat === "ok" ? "✓ Terminé" : stat === "wip" ? "En cours" : "⚠️ À faire"}
            </div>
          </div>
        </div>
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center text-white font-bold shadow-lg border-4 border-white z-10" style={{ backgroundColor: p.c }}>{p.n}</div>
      </div>
      {!last && <div className="flex items-center h-full pt-24"><div className="w-6 border-t-2 border-dashed" style={{ borderColor: p.c }} /><span className="text-gray-400">→</span></div>}
    </div>
  );
}

// Vue Pas à pas verticale
function ViewPasAPas({ client, etapes, onClose, onToggle }) {
  const ref = useRef(null);
  const [exp, setExp] = useState(false);
  
  const phases = [
    { n: 1, t: "Mon projet démarre", c: "#22C55E", bg: "#DCFCE7", e: [["datePriseRdv", "Prise de RDV"], ["dateR1", "Visite R1"], ["resultatAppelQualite", "Appel qualité"]] },
    { n: 2, t: "Mon dossier est monté", c: "#8B5CF6", bg: "#EDE9FE", e: [["transfereAmo", "Transfert AMO"], ["vtAmo", "VT AMO"], ["etatDevis", "Devis établi"]] },
    { n: 3, t: "Validation technique", c: "#3B82F6", bg: "#DBEAFE", e: [["dateR2", "Visite R2"], ["statutR2", "Validation"]] },
    { n: 4, t: "Aides financières", c: "#F59E0B", bg: "#FEF3C7", e: [["dateDepotAnah", "Dépôt ANAH"], ["dateOctroi", "Octroi"], ["dateEncaissementAvance", "Avance"]] },
    { n: 5, t: "Travaux et réception", c: "#6B7280", bg: "#F3F4F6", e: [["dateDebutChantier", "Travaux"], ["dateReceptionChantier", "Réception"], ["reserveOuPas", "Terminé"]] }
  ];
  
  let total = 0;
  let inProg = null;
  for (let p of phases) {
    const ids = p.e.map(x => x[0]);
    const done = countDone(ids, etapes);
    if (done === ids.length) total++;
    else if (done > 0 && !inProg) inProg = p.t;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto">
      <div className="relative w-full max-w-lg">
        <div className="absolute -top-3 -right-3 z-20 flex gap-2">
          <button onClick={async () => { setExp(true); await exportPDF(ref, client.nom + "_" + client.prenom); setExp(false); }} disabled={exp} className="w-11 h-11 bg-green-500 hover:bg-green-400 disabled:bg-green-700 rounded-full shadow-lg flex items-center justify-center">
            {exp ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Download className="w-5 h-5 text-white" />}
          </button>
          <button onClick={onClose} className="w-11 h-11 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100"><X className="w-5 h-5 text-gray-600" /></button>
        </div>
        <div ref={ref} className="rounded-3xl shadow-2xl overflow-hidden" style={{ background: 'linear-gradient(180deg, #F5F9F5 0%, #FDF8F3 100%)' }}>
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #FFD93D 0%, #F7B731 100%)' }}><Home className="w-7 h-7 text-green-700" /></div>
              <div><h1 className="text-xl font-bold text-gray-800">Votre parcours</h1><h2 className="text-lg font-semibold text-green-700">{"d'adaptation du logement"}</h2></div>
            </div>
            <div className="bg-white/90 rounded-xl px-4 py-3 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div><p className="font-bold text-gray-800">{client.nom} {client.prenom}</p><p className="text-sm text-gray-500">Dép. {client.dpt}</p></div>
                <div className="px-3 py-1.5 rounded-full font-bold text-white" style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}>{total}/5</div>
              </div>
            </div>
          </div>
          <div className="px-4 pb-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-3">
              <VerticalCard p={phases[0]} etapes={etapes} last={false} />
              <VerticalCard p={phases[1]} etapes={etapes} last={false} />
              <VerticalCard p={phases[2]} etapes={etapes} last={false} />
              <VerticalCard p={phases[3]} etapes={etapes} last={false} />
              <VerticalCard p={phases[4]} etapes={etapes} last={true} />
            </div>
            <div className="mt-5 p-4 rounded-xl shadow-sm" style={{ background: total === 5 ? 'linear-gradient(135deg, #DCFCE7, #D1FAE5)' : 'linear-gradient(135deg, #F0FDF4, #ECFDF5)', border: '1px solid #BBF7D0' }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}>
                  {total === 5 ? <span className="text-xl">🎉</span> : <Clock className="w-6 h-6 text-white" />}
                </div>
                <div><p className="font-semibold text-green-800">{total === 5 ? 'Félicitations !' : inProg ? "En cours : " + inProg : 'Votre dossier avance'}</p><p className="text-sm text-green-600">{"SANI ADAPT • MaPrimeAdapt'"}</p></div>
              </div>
            </div>
          </div>
        </div>
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-white/95 backdrop-blur rounded-full shadow-xl px-2 py-1.5 flex gap-1 border border-gray-200">
          <button onClick={onToggle} className="px-4 py-2 rounded-full text-sm font-medium text-gray-500 hover:bg-gray-100">📜 Parchemin</button>
          <button className="px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-700">📋 Pas à pas</button>
        </div>
      </div>
    </div>
  );
}

// Carte verticale pour Pas à pas
function VerticalCard({ p, etapes, last }) {
  const ids = p.e.map(x => x[0]);
  const done = countDone(ids, etapes);
  const stat = done === ids.length ? "ok" : done > 0 ? "wip" : "todo";
  const isPending = stat === "todo";

  const e0 = { id: p.e[0][0], label: p.e[0][1], val: etapes[p.e[0][0]], done: !!(etapes[p.e[0][0]] && etapes[p.e[0][0]].trim()) };
  const e1 = { id: p.e[1][0], label: p.e[1][1], val: etapes[p.e[1][0]], done: !!(etapes[p.e[1][0]] && etapes[p.e[1][0]].trim()) };
  const e2 = p.e[2] ? { id: p.e[2][0], label: p.e[2][1], val: etapes[p.e[2][0]], done: !!(etapes[p.e[2][0]] && etapes[p.e[2][0]].trim()) } : null;

  return (
    <div className={"relative transition-all " + (isPending ? 'opacity-60' : '')}>
      {!last && <div className="absolute left-[27px] top-[60px] bottom-[-12px] w-[3px] rounded" style={{ backgroundColor: stat === "ok" ? p.c : '#E5E7EB' }} />}
      <div className="absolute left-[21px] top-4 w-[15px] h-[15px] rounded-full z-20 border-[3px] border-white shadow-sm flex items-center justify-center" style={{ backgroundColor: stat !== "todo" ? p.c : '#E5E7EB' }}>
        {stat === "ok" && <CheckCircle className="w-2.5 h-2.5 text-white" />}
      </div>
      <div className="ml-12 rounded-2xl overflow-hidden shadow-sm" style={{ background: "linear-gradient(135deg, " + p.bg + " 0%, white 100%)", border: "2px solid " + (isPending ? '#E5E7EB' : p.c + "40") }}>
        <div className="px-4 py-2.5 flex items-center gap-3" style={{ background: p.c }}>
          <div className="w-8 h-8 rounded-lg bg-white/25 flex items-center justify-center"><span className="text-white font-bold text-lg">{p.n}</span></div>
          <span className="flex-1 text-white font-bold">{p.t}</span>
          <div className="flex gap-1">
            <div className={"w-2 h-2 rounded-full " + (e0.done ? "bg-white" : "bg-white/30")} />
            <div className={"w-2 h-2 rounded-full " + (e1.done ? "bg-white" : "bg-white/30")} />
            {e2 && <div className={"w-2 h-2 rounded-full " + (e2.done ? "bg-white" : "bg-white/30")} />}
          </div>
        </div>
        <div className="px-4 py-3">
          <div className="space-y-2">
            <EtapeRow e={e0} c={p.c} />
            <EtapeRow e={e1} c={p.c} />
            {e2 && <EtapeRow e={e2} c={p.c} />}
          </div>
          <div className="mt-3 flex justify-end">
            {stat === "ok" && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700"><CheckCircle className="w-4 h-4" />Terminé</span>}
            {stat === "wip" && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: p.bg, color: p.c }}><Clock className="w-4 h-4" />En cours</span>}
            {stat === "todo" && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-500"><Lock className="w-3.5 h-3.5" />À venir</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Ligne d'étape
function EtapeRow({ e, c }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className={"w-5 h-5 rounded flex-shrink-0 flex items-center justify-center mt-0.5 " + (e.done ? "shadow-sm" : "border-2")} style={{ background: e.done ? c : 'white', borderColor: e.done ? 'transparent' : '#D1D5DB' }}>
        {e.done && <CheckCircle className="w-3.5 h-3.5 text-white" />}
      </div>
      <div className="flex-1">
        <span className={"text-sm " + (e.done ? "text-gray-700" : "text-gray-400")}>{e.label}</span>
        {e.val && e.done && <span className="text-xs text-gray-400 ml-1">({e.val})</span>}
      </div>
    </div>
  );
}

// Modal principal
function Modal({ parcours, onClose }) {
  const [v, setV] = useState('parchemin');
  if (!parcours) return null;
  if (v === 'parchemin') return <ViewParchemin client={parcours.client} etapes={parcours.etapes} onClose={onClose} onToggle={() => setV('pasapas')} />;
  return <ViewPasAPas client={parcours.client} etapes={parcours.etapes} onClose={onClose} onToggle={() => setV('parchemin')} />;
}

// Composant principal
export function ParcoursClientParchemin() {
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [parcours, setParcours] = useState(null);
  const [lp, setLp] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [statuts, setStatuts] = useState([]);
  const [statutFilter, setStatutFilter] = useState("");
  const ITEMS_PER_PAGE = 50;

  async function search(query, pageNum = 1, append = false, statut = "") {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: query,
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        statut: statut
      });
      const res = await authFetch(API + "/api/parcours/search?" + params.toString());
      const d = await res.json();
      const newClients = d.clients || [];
      if (append) {
        setClients(prev => [...prev, ...newClients]);
      } else {
        setClients(newClients);
      }
      setTotal(d.total || newClients.length);
      setHasMore(d.hasMore || false);
      if (d.statuts && !append) {
        setStatuts(d.statuts);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function load(row) {
    setLp(true);
    try {
      const res = await authFetch(API + "/api/parcours/client/" + row);
      const d = await res.json();
      setParcours(d);
    } catch (e) { console.error(e); }
    setLp(false);
  }

  function loadMore() {
    const nextPage = page + 1;
    setPage(nextPage);
    search(q, nextPage, true, statutFilter);
  }

  function handleStatutChange(newStatut) {
    setStatutFilter(newStatut);
    setPage(1);
    search(q, 1, false, newStatut);
  }

  useEffect(() => { search("", 1, false, ""); }, []);
  useEffect(() => { 
    setPage(1); 
    const t = setTimeout(() => search(q, 1, false, statutFilter), 300); 
    return () => clearTimeout(t); 
  }, [q]);

  function color(etape) {
    if (etape.includes("Terminé")) return "bg-green-500";
    if (etape.includes("Chantier")) return "bg-amber-500";
    if (etape.includes("Octroi")) return "bg-purple-500";
    if (etape.includes("R2")) return "bg-indigo-500";
    if (etape.includes("AMO")) return "bg-blue-500";
    if (etape.includes("R1")) return "bg-cyan-500";
    if (etape.includes("RDV")) return "bg-slate-500";
    if (etape.includes("Qualité")) return "bg-teal-500";
    return "bg-slate-500";
  }

  function formatAnciennete(jours) {
    if (jours === null || jours === undefined) return null;
    if (jours === 0) return "Aujourd'hui";
    if (jours === 1) return "Hier";
    if (jours < 7) return `${jours}j`;
    if (jours < 30) return `${Math.floor(jours / 7)} sem.`;
    if (jours < 365) return `${Math.floor(jours / 30)} mois`;
    return `${Math.floor(jours / 365)} an${Math.floor(jours / 365) > 1 ? 's' : ''}`;
  }

  function ancienneteColor(jours) {
    if (jours === null || jours === undefined) return "text-slate-400";
    if (jours <= 7) return "text-green-400";
    if (jours <= 30) return "text-yellow-400";
    if (jours <= 90) return "text-orange-400";
    return "text-red-400";
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
      <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 p-4 border-b border-green-700/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center"><Home className="w-5 h-5 text-white" /></div>
          <div>
            <h3 className="text-lg font-bold text-green-100">📜 Parcours Client</h3>
            <p className="text-xs text-green-200/60">{"Suivez l'avancement des projets"} • {total} client{total > 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>
      
      {/* Filtres */}
      <div className="p-4 border-b border-slate-700/50 space-y-3">
        {/* Recherche texte */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom, prénom, dpt, tél..." className="w-full pl-10 pr-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-green-500" />
        </div>
        
        {/* Filtre par statut */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleStatutChange("")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              !statutFilter ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Tous
          </button>
          {statuts.map(s => (
            <button
              key={s}
              onClick={() => handleStatutChange(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statutFilter === s ? color(s) + ' text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-[70vh] overflow-auto">
        {loading && clients.length === 0 ? (
          <div className="p-8 text-center text-slate-400"><div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-2" />Chargement...</div>
        ) : clients.length === 0 ? (
          <div className="p-8 text-center text-slate-400">Aucun client</div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {clients.map((c, idx) => (
              <ClientRow key={c.rowNumber || idx} c={c} color={color} sel={sel} setSel={setSel} load={load} lp={lp} formatAnciennete={formatAnciennete} ancienneteColor={ancienneteColor} />
            ))}
            {hasMore && (
              <div className="p-4 text-center">
                <button 
                  onClick={loadMore} 
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 rounded-lg text-white text-sm flex items-center gap-2 mx-auto"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Chargement...</>
                  ) : (
                    <>Charger plus ({total - clients.length} restants)</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {parcours && <Modal parcours={parcours} onClose={() => { setParcours(null); setSel(null); }} />}
    </div>
  );
}

function ClientRow({ c, color, sel, setSel, load, lp, formatAnciennete, ancienneteColor }) {
  if (!c) return null;
  const anciennete = formatAnciennete ? formatAnciennete(c.joursStatut) : null;
  const ancColor = ancienneteColor ? ancienneteColor(c.joursStatut) : "text-slate-400";
  
  return (
    <div className="p-3 hover:bg-slate-700/30 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-white font-medium truncate">{c.nom} {c.prenom}</p>
          <span className="text-xs text-slate-500">({c.dpt})</span>
        </div>
        <div className="flex items-center gap-3 mt-1">
          {c.telephone ? (
            <span className="text-xs text-blue-400 flex items-center gap-1">
              <Phone className="w-3 h-3" />{c.telephone}
            </span>
          ) : (
            <span className="text-xs text-red-400">⚠️ Tél. manquant</span>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        <span className={"text-xs px-2 py-1 rounded-full text-white " + color(c.etapeActuelle)}>
          {c.etapeActuelle}
        </span>
        {anciennete && (
          <span className={`text-xs flex items-center gap-1 ${ancColor}`}>
            <Clock className="w-3 h-3" />
            {anciennete}
          </span>
        )}
      </div>
      <button 
        onClick={() => { setSel(c); load(c.rowNumber); }} 
        disabled={lp && sel?.rowNumber === c.rowNumber} 
        className="flex-shrink-0 p-2 bg-green-600 hover:bg-green-500 rounded-lg disabled:opacity-50"
      >
        {lp && sel?.rowNumber === c.rowNumber ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Map className="w-5 h-5 text-white" />
        )}
      </button>
    </div>
  );
}

export default ParcoursClientParchemin;
